import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import {
  buildReverseIndex,
  findIdentityById,
  mergeEmailUpdate,
  projectEmailsFromIndex,
  upsertEmailIndex,
} from '@/lib/emailMerging';
import type {
  ContactAiReviewRecommendation,
  Email,
  RealtimeEvent,
  ThreadMessageWithStatus,
} from '@/types/email';
import { fetchEmail } from '@/api/emails';
import { useAccountStore } from '@/stores/accountStore';
import { useAttachmentStore } from '@/stores/attachmentStore';
import { useBriefingStore } from '@/stores/briefingStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useEmailStore } from '@/stores/emailStore';
import { useFolderStore } from '@/stores/folderStore';
import { useNotesStore } from '@/stores/notesStore';
import { useSearchStore } from '@/stores/searchStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useSyncStatusStore } from '@/stores/syncStatusStore';
import {
  removeEmailsFromStore,
  isRecentlyRemoved,
  isSenderBeingArchived,
  isRealtimeUpdateSuppressed,
  confirmServerFlags,
  applyOverlayToEmail,
  visibleEmailFilter,
} from '@/stores/emailStateHelpers';
import { isEmailOperationInFlight, isStaleMoveEcho, whenEmailIdle } from '@/stores/emailCommands';
import { useContactStore } from '@/stores/contactStore';
import { remapMergedContacts } from '@/stores/contactStateSync';
import { mailEvents } from '@/events/mailEvents';
import { RealtimeEventBatcher } from './eventBatcher';

export interface RealtimeEventContext {
  scheduleMailboxReload: (accountId: string, folderId: string) => void;
}

// Create batcher instance (singleton)
let eventBatcher: RealtimeEventBatcher | null = null;
let currentContext: RealtimeEventContext | null = null;

/**
 * Apply a run of consecutive email.received/email.updated payloads. Routed
 * through the store's single upsert applier (index + reverse index +
 * suppression-filtered projection + selectedEmail/Thread sync).
 */
function applyEmailUpdateBatch(emailUpdates: Email[]) {
  if (!emailUpdates.length) {
    return;
  }
  const emailsToUpsert: Email[] = [];
  const removalCandidates: Email[] = [];

  emailUpdates.forEach((email) => {
    if (shouldDisplayEmail(email)) {
      emailsToUpsert.push(email);
    } else if (emailIndexHasId(email.id, email.accountId)) {
      removalCandidates.push(email);
    }
  });

  // Upsert-wins veto (R2): a payload for the same id that DID pass in this
  // batch means the message still belongs here — Gmail's per-label fan-out can
  // pair the on-view Inbox copy (passing) with the off-view All-Mail copy
  // (failing) in one batch, and the failing sibling must not evict the row.
  const passingIds = new Set(emailsToUpsert.map((email) => email.id));

  const storeState = useEmailStore.getState();
  const removalIds: string[] = [];
  removalCandidates.forEach((email) => {
    if (passingIds.has(email.id)) {
      return;
    }
    // Displayed-copy guard (R2, mirrors handleEmailDeletedEvent): an
    // email.updated describes ONE label membership. If the copy we're
    // displaying belongs to a DIFFERENT folder than this payload's folderId,
    // the message did not leave the folder in view — a flag/label change on an
    // off-view label must never evict the on-view row (the reader-bounce).
    if (email.folderId != null) {
      const identity = findIdentityById(storeState.emailReverseIndex, email.id);
      const existing = identity ? storeState.emailIndex[identity] : null;
      if (existing && existing.folderId != null && existing.folderId !== email.folderId) {
        return;
      }
    }
    // Echo shield (R5c): a stale email.updated still carrying the pre-undo /
    // pre-action folder must not evict a row the engine just restored. If this
    // payload's folder positively disagrees with the engine's last recorded
    // placement for this id (within the shield window), it's a stale echo —
    // skip the eviction (the move handler already applies this to email.moved).
    if (isStaleMoveEcho(email.id, email.accountId, email.folderId ?? null)) {
      return;
    }
    removalIds.push(email.id);
  });

  // Apply the passing upserts FIRST so a surviving copy is present before any
  // eviction side effects (selection sync) run — no transient drop-then-re-add
  // within one batch.
  if (emailsToUpsert.length > 0) {
    useEmailStore.getState().applyEmailUpserts(emailsToUpsert);
  }

  if (removalIds.length > 0) {
    // PASSIVE eviction (a server rule/another client moved the message): no
    // 60s "recently removed" stamp — the message legitimately lives elsewhere
    // now and must be visible there immediately. Passive removals never touch
    // the open reader (initiator derived from markRemoved: false in
    // removeEmailsFromStore).
    removeEmailsFromStore(removalIds, { markRemoved: false });
    removalIds.forEach((id) => {
      pruneBulkSelection([id]);
      pruneSenderDigestEmails([id]);
    });
  }

  emailUpdates.forEach((email) => {
    updateSearchResultsWithEmail(email);
    updateSenderDigestEmail(email);
  });

  // If any new emails are from pending senders, refresh the pending contacts
  // list so the NewSendersBanner appears without needing a page reload.
  const hasPendingEmails = emailUpdates.some(
    (email) => email.contactApprovalState === 'pending'
  );
  if (hasPendingEmails) {
    const contactStore = useContactStore.getState();
    const accountId = useAccountStore.getState().selectedAccountId;
    const folderId = useFolderStore.getState().selectedFolderId();
    if (accountId && folderId && contactStore.pendingContactsLoaded) {
      void contactStore.loadPendingContacts({ accountId, folderId, force: true });
    }
  }
}

// Short-lived record of server-driven deletions (N25): the move handler's
// ASYNC hydration fetch resolves outside the batch's arrival-order guarantee,
// so a move-then-delete batch could re-add the deleted message when the fetch
// landed. The hydration consults this before applying.
const recentlyDeletedByServer = new Map<string, number>();
const RECENT_DELETE_WINDOW_MS = 60_000;

function markServerDeleted(emailId: string): void {
  recentlyDeletedByServer.set(emailId, Date.now());
  if (recentlyDeletedByServer.size > 500) {
    const cutoff = Date.now() - RECENT_DELETE_WINDOW_MS;
    recentlyDeletedByServer.forEach((at, id) => {
      if (at < cutoff) {
        recentlyDeletedByServer.delete(id);
      }
    });
  }
}

function wasServerDeletedRecently(emailId: string): boolean {
  const at = recentlyDeletedByServer.get(emailId);
  if (!at) return false;
  if (Date.now() - at > RECENT_DELETE_WINDOW_MS) {
    recentlyDeletedByServer.delete(emailId);
    return false;
  }
  return true;
}

function handleEmailDeletedEvent(
  event: { emailId: string; accountId?: string; folderId?: string }
) {
  // A per-mailbox delete (e.g. a Gmail label removed) must only drop the email
  // from the folder it actually left — NOT globally. getEmailIdentity keys one
  // entry per message id, so a blind id-prune would evict a multi-label message
  // from every view (inbox + unified) even though it still lives elsewhere.
  // Only remove when the displayed email belongs to the event's folder, or when
  // we can't tell (no folderId on the event, or the email isn't currently loaded).
  const { emailId, accountId, folderId } = event;
  const storeState = useEmailStore.getState();
  if (folderId) {
    const identity = findIdentityById(storeState.emailReverseIndex, emailId);
    const existing = identity ? storeState.emailIndex[identity] : null;
    if (existing && existing.folderId != null && existing.folderId !== folderId) {
      return;
    }
  }

  // Server-driven removal: no "recently removed" stamp, so a later redelivery
  // event (processed in arrival order) can legitimately re-add the message.
  // (The async move-hydration is NOT arrival-ordered — it checks the
  // server-delete record instead, N25.)
  markServerDeleted(emailId);
  removeEmailsFromStore([emailId], { markRemoved: false });
  pruneBulkSelection([emailId]);
  pruneSenderDigestEmails([emailId]);
  if (accountId) {
    removeSearchResultsByEmailIds(new Set([emailId]), accountId);
  }
}

function processBatchedEvents(events: RealtimeEvent[]) {
  if (!currentContext) return;

  // Events are processed in ARRIVAL ORDER. Grouping by type (updates → deletes
  // → moves) inverted server sequencing: a move-then-delete resurrected the
  // message, a delete-then-redeliver wrongly dropped it. Consecutive update
  // payloads are still batched into one store write; deletes/moves act as
  // ordering barriers that flush the pending batch first.
  const attachmentEvents: Array<Extract<RealtimeEvent, { type: 'attachment.downloaded' }>> = [];
  const otherEvents: RealtimeEvent[] = [];
  let updateBatch: Email[] = [];

  const flushUpdateBatch = () => {
    if (!updateBatch.length) {
      return;
    }
    const batch = updateBatch;
    updateBatch = [];
    applyEmailUpdateBatch(batch);
  };

  events.forEach((event) => {
    switch (event.type) {
      case 'email.received':
      case 'email.updated':
        if (event.email) {
          let email = applyDownloadedAttachmentState(event.email);
          if (event.type === 'email.updated' && isRealtimeUpdateSuppressed(email.id)) {
            // Echo of our own optimistic change. MERGE, don't skip: the echo
            // carries non-flag fields (autoLabels, aiAnnotations, approval
            // state) that must land. Confirm any flag the server now agrees
            // with (clears that overlay entry), then re-assert the remaining
            // un-flushed optimistic flags on top of the payload so a stale
            // echo can't revert them.
            confirmServerFlags(email.id, email);
            email = applyOverlayToEmail(email);
          }
          updateBatch.push(email);
        }
        break;
      case 'email.deleted':
        if (event.emailId) {
          flushUpdateBatch();
          handleEmailDeletedEvent({
            emailId: event.emailId,
            accountId: event.accountId,
            folderId: event.folderId,
          });
        }
        break;
      case 'email.moved':
        flushUpdateBatch();
        handleEmailMoved(event);
        break;
      case 'attachment.downloaded':
        attachmentEvents.push(event);
        break;
      default:
        otherEvents.push(event);
        break;
    }
  });
  flushUpdateBatch();

  // Attachment + non-email events are order-insensitive relative to list state
  attachmentEvents.forEach((event) => {
    handleAttachmentDownloaded(event);
  });

  otherEvents.forEach((event) => {
    switch (event.type) {
      case 'mailbox.counts':
        handleMailboxCounts(event);
        break;
      case 'folder.preferences.updated':
        handleFolderPreferencesUpdated(event);
        break;
      case 'briefing.updated':
        useBriefingStore.getState().handleBriefingUpdatedEvent(event.accountId);
        break;
      case 'mailbox.list.changed':
        handleMailboxListChanged(event);
        break;
      case 'mailbox.sync.progress':
        useSyncStatusStore.getState().updateProgressFromEvent(event);
        break;
      case 'sync.status':
        useSyncStatusStore.getState().updateStatusFromEvent({ status: event.status });
        break;
      case 'sync.job.updated':
        useSyncStatusStore.getState().updateJob(event.job);
        break;
      case 'contact.stateChanged':
        handleContactStateChanged(event);
        break;
      case 'contact.reviewRecommendation':
        handleContactReviewRecommendation(event);
        break;
      case 'contact.merged':
        remapMergedContacts(event.mergedContactIds, event.contactId, event.approvedState);
        break;
      case 'calendar.event.created':
      case 'calendar.event.updated':
        handleCalendarEventUpsert(event);
        break;
      case 'calendar.event.deleted':
        handleCalendarEventDeleted(event);
        break;
      case 'note.created':
      case 'note.updated':
        handleNoteUpserted(event);
        break;
      case 'note.deleted':
        handleNoteDeleted(event);
        break;
      case 'note.tag.created':
      case 'note.tag.updated':
        handleNoteTagUpsert(event);
        break;
      case 'note.tag.deleted':
        handleNoteTagDeleted(event);
        break;
      case 'operation.failed':
        handleOperationFailed(event);
        break;
      case 'resync':
        handleResync();
        break;
    }
  });
}

// Full, deterministic reload triggered by the server's 'resync' control event
// when the client's missed events were evicted from the replay buffer. Reloads
// folder counts and the current mailbox so no silently-dropped change lingers.
function handleResync() {
  const accountId = useAccountStore.getState().selectedAccountId;
  const folderId = useFolderStore.getState().selectedFolderId();
  if (accountId) {
    useFolderStore.getState().loadFolders(accountId).catch(() => undefined);
  }
  if (currentContext && accountId && folderId) {
    currentContext.scheduleMailboxReload(accountId, folderId);
  }
}

export async function handleRealtimeEvent(event: RealtimeEvent, ctx: RealtimeEventContext, immediate = false) {
  if (!shouldProcessEvent(event)) {
    return;
  }

  // Store context for batcher
  currentContext = ctx;

  // Initialize batcher on first use
  if (!eventBatcher) {
    eventBatcher = new RealtimeEventBatcher(processBatchedEvents, 50);
  }

  // Process immediately for critical events or if requested
  if (
    immediate ||
    event.type === 'mailbox.counts' ||
    event.type === 'sync.status' ||
    event.type === 'resync'
  ) {
    processBatchedEvents([event]);
    return;
  }

  // Otherwise batch it
  eventBatcher.addEvent(event);
}

const emailIndexHasId = (id: string, accountId?: string | null): boolean => {
  const state = useEmailStore.getState();
  if (accountId) {
    return `${accountId}:${id}` in state.emailIndex;
  }
  // Use reverse index for O(1) lookup when accountId is unavailable
  return state.emailReverseIndex.has(id);
};

function shouldProcessEvent(event: RealtimeEvent): boolean {
  if (event.type === 'sync.status' || event.type === 'mailbox.counts' || event.type === 'resync') {
    return true;
  }

  const selectedAccountId = useAccountStore.getState().selectedAccountId;
  if (!selectedAccountId) {
    return false;
  }

  if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
    return true;
  }

  if ('accountId' in event) {
    return event.accountId === selectedAccountId;
  }

  return true;
}

async function handleEmailReceivedOrUpdated(rawEmail: Email) {
  // A genuine (re)delivery supersedes any recent server-side delete (the
  // events are arrival-ordered; only the async move-hydration is not).
  recentlyDeletedByServer.delete(rawEmail.id);
  // Same overlay merge as the batched update path (E9 — N25 closed the gap):
  // clear overlay entries the server now agrees with, then re-assert the
  // remaining optimistic flags. Without this a move-in hydration rendered
  // stale server flags until the flag op's echo arrived.
  confirmServerFlags(rawEmail.id, rawEmail);
  const email = applyOverlayToEmail(applyDownloadedAttachmentState(rawEmail));

  if (shouldDisplayEmail(email)) {
    // Single writer: index + reverse index + projection + selection sync.
    useEmailStore.getState().applyEmailUpserts([email]);
  } else if (emailIndexHasId(email.id, email.accountId)) {
    // Passive eviction — no 60s grace stamp (see applyEmailUpdateBatch).
    removeEmailsFromStore([email.id], { markRemoved: false });
  }

  updateSearchResultsWithEmail(email);
  updateSenderDigestEmail(email);
}

/**
 * Compute archive/trash status based on folder role.
 * Mirrors the logic in threadsService.js getThreadDetail().
 */
function computeMessageStatus(accountId: string, folderId: string): { isArchived: boolean; isTrashed: boolean } {
  const { accountFolderCache } = useFolderStore.getState();
  const accountFolders = accountFolderCache[accountId] ?? [];
  const folder = accountFolders.find((f) => f.path === folderId || f.id === folderId);

  if (!folder) {
    return { isArchived: false, isTrashed: false };
  }

  const role = folder.role ?? null;
  const activeRoles = ['inbox', 'sent', 'drafts'];
  const isInActiveFolder = role ? activeRoles.includes(role) : false;
  const isTrashed = role === 'trash';
  const isArchived = !isInActiveFolder && !isTrashed && role === 'archive';

  return { isArchived, isTrashed };
}

async function handleEmailMoved(
  event: Extract<RealtimeEvent, { type: 'email.moved' }>,
  hasDeferred = false
) {
  // An engine operation (action/undo/redo) is queued or running for this id.
  // The event may be the op's own echo OR a legitimate external move racing
  // it — we can't tell yet, so DEFER (don't drop, N10) until the id's chain
  // settles, then let the echo shield below classify it. One deferral only:
  // still-busy after that means the op's completion settles placement.
  if (isEmailOperationInFlight(event.emailId)) {
    if (!hasDeferred) {
      void whenEmailIdle(event.emailId).then(() => handleEmailMoved(event, true));
      return;
    }
    console.log('[handleEmailMoved] Skipping move event — operation still in flight for', event.emailId);
    return;
  }
  // Echo shield (E7): IMAP echoes lag 10–30s, far beyond the op's network
  // window. A move whose destination disagrees with where the engine last
  // placed this id is the stale echo of an earlier (possibly undone) action,
  // not new truth — processing it would prune the restored row.
  if (isStaleMoveEcho(event.emailId, event.accountId, event.toFolderId)) {
    console.log('[handleEmailMoved] Skipping stale move echo for', event.emailId);
    return;
  }
  console.log('[handleEmailMoved] Processing move event:', {
    emailId: event.emailId,
    fromFolderId: event.fromFolderId,
    toFolderId: event.toFolderId,
  });
  // Build a representative Email for shouldDisplayEmail using existing state
  // if available, so that blocked-sender and flagged-survey filters work correctly
  const storeState = useEmailStore.getState();
  const existingIdentity = findIdentityById(storeState.emailReverseIndex, event.emailId);
  const existingEmail = existingIdentity ? storeState.emailIndex[existingIdentity] : null;
  const movedEmail: Email = existingEmail
    ? { ...existingEmail, folderId: event.toFolderId }
    : { id: event.emailId, accountId: event.accountId, folderId: event.toFolderId } as Email;
  const shouldInclude = shouldDisplayEmail(movedEmail);
  console.log('[handleEmailMoved] shouldInclude:', shouldInclude);

  // Compute new archive/trash status based on destination folder
  const { isArchived, isTrashed } = computeMessageStatus(event.accountId, event.toFolderId);

  // Multi-label guard (E6, Gmail): a message can live in several labels. A
  // move event describes ONE label transition — if the displayed copy belongs
  // to a DIFFERENT folder than the move's source, the message did not leave
  // the folder we're showing; don't prune or re-folder it. (Computed before
  // the store writes so the eviction can route through removeEmailsFromStore.)
  const preState = useEmailStore.getState();
  const preIdentity = findIdentityById(preState.emailReverseIndex, event.emailId);
  const preMatch = preIdentity ? preState.emailIndex[preIdentity] : null;
  const displayedElsewhereGuard =
    Boolean(preMatch) &&
    preMatch!.folderId != null &&
    event.fromFolderId != null &&
    preMatch!.folderId !== event.fromFolderId;

  // Passive eviction routes through removeEmailsFromStore (A4/N19): the
  // inline index prune skipped the mailbox offset decrement and the cache
  // prune, so the next load-more skipped/duplicated rows.
  if (preMatch && !shouldInclude && !displayedElsewhereGuard) {
    removeEmailsFromStore([event.emailId], { markRemoved: false });
  }

  useEmailStore.setState((state) => {
    const matchIdentity = findIdentityById(state.emailReverseIndex, event.emailId);
    const match = matchIdentity ? state.emailIndex[matchIdentity] : null;
    if (!match && !preMatch) {
      return {};
    }
    const displayedElsewhere = displayedElsewhereGuard;

    const updates: Partial<ReturnType<typeof useEmailStore.getState>> = {};
    if (match && shouldInclude && !displayedElsewhere) {
      const updated = { ...match, folderId: event.toFolderId };
      const nextIndex = upsertEmailIndex(state.emailIndex, [updated]);
      updates.emailIndex = nextIndex;
      updates.emailReverseIndex = buildReverseIndex(nextIndex);
      updates.emails = projectEmailsFromIndex(nextIndex, { visibilityFilter: visibleEmailFilter });
    }

    // Update thread message with new folder AND status. mailboxPaths is a
    // SET of locations — transition fromFolderId→toFolderId inside it rather
    // than overwriting it with the single destination (E6).
    const threadMessage = state.selectedThread?.messages.find((m) => m.id === event.emailId);
    const existingPaths = (threadMessage as { mailboxPaths?: string[] } | undefined)?.mailboxPaths;
    const nextPaths = Array.isArray(existingPaths)
      ? Array.from(
          new Set(
            existingPaths
              .filter((path) => path !== event.fromFolderId)
              .concat(event.toFolderId)
          )
        )
      : [event.toFolderId];

    const threadUpdate = {
      id: event.emailId,
      accountId: event.accountId,
      folderId: event.toFolderId,
      isArchived,
      isTrashed,
      mailboxPaths: nextPaths,
    } as unknown as Email;

    const nextThread = updateThreadMessage(state.selectedThread, threadUpdate);
    if (nextThread !== state.selectedThread) {
      updates.selectedThread = nextThread;
    }

    // S4 / D15: an external move of the OPEN message must not close the reader.
    // Keep it open on the message (which still exists — it moved, it wasn't
    // deleted) and just track its new folder. Gated on displayedElsewhere so a
    // move affecting a DIFFERENT label of a multi-label message leaves the
    // displayed copy untouched.
    if (state.selectedEmail?.id === event.emailId && !displayedElsewhere) {
      updates.selectedEmail = {
        ...state.selectedEmail,
        folderId: event.toFolderId,
      };
    }

    return updates;
  });

  pruneBulkSelection([event.emailId]);
  updateSearchResultsForMovedEmail(event);
  pruneSenderDigestEmails([event.emailId]);

  if (shouldInclude) {
    const postState = useEmailStore.getState();
    const postIdentity = findIdentityById(postState.emailReverseIndex, event.emailId);
    const email = postIdentity ? postState.emailIndex[postIdentity] : null;
    if (!email) {
      try {
        const fetched = await fetchEmail(event.accountId, event.emailId, event.toFolderId);
        // The fetch resolves OUTSIDE the batch's arrival-order guarantee: a
        // delete processed meanwhile must win, or a move-then-delete batch
        // resurrects the message (N25).
        if (wasServerDeletedRecently(event.emailId)) {
          return;
        }
        await handleEmailReceivedOrUpdated(fetched);
      } catch (error) {
        console.error('Failed to hydrate moved email', error);
      }
    }
  }
}

function handleAttachmentDownloaded(
  event: Extract<RealtimeEvent, { type: 'attachment.downloaded' }>
) {
  if (!event.accountId) {
    return;
  }
  useAttachmentStore
    .getState()
    .markDownloaded(event.accountId, event.emailId, event.attachmentId);

  const patch = (entity: Email | ThreadMessageWithStatus | null): Email | ThreadMessageWithStatus | null => {
    if (!entity || entity.id !== event.emailId) {
      return entity;
    }
    const updated = setAttachmentDownloadedFlag(entity, event.attachmentId, event.metadata);
    return updated ?? entity;
  };

  useEmailStore.setState((state) => {
    // Update emailIndex (source of truth) so attachment state survives re-projection
    let nextIndex = state.emailIndex;
    let indexChanged = false;
    const identity = findIdentityById(state.emailReverseIndex, event.emailId);
    if (identity) {
      const email = state.emailIndex[identity];
      if (email) {
        const patched = patch(email) as Email;
        if (patched !== email) {
          nextIndex = { ...state.emailIndex, [identity]: patched };
          indexChanged = true;
        }
      }
    }

    const nextSelectedEmail = patch(state.selectedEmail);
    const selectedEmailChanged = nextSelectedEmail !== state.selectedEmail;

    let nextThread = state.selectedThread;
    let threadChanged = false;
    if (state.selectedThread) {
      const messages = state.selectedThread.messages.map(
        (message) => patch(message) as ThreadMessageWithStatus
      );
      threadChanged = messages.some(
        (message, index) => message !== state.selectedThread!.messages[index]
      );
      if (threadChanged) {
        nextThread = {
          ...state.selectedThread,
          messages,
          unreadCount: messages.filter((message) => !message.read).length,
        };
      }
    }

    const updates: Partial<ReturnType<typeof useEmailStore.getState>> = {};
    if (indexChanged) {
      updates.emailIndex = nextIndex;
      updates.emails = projectEmailsFromIndex(
        nextIndex,
        { visibilityFilter: visibleEmailFilter },
        state.emails
      );
    }
    if (selectedEmailChanged) {
      updates.selectedEmail = nextSelectedEmail;
    }
    if (threadChanged) {
      updates.selectedThread = nextThread;
    }
    return updates;
  });

  useSearchStore.setState((state) => {
    let changed = false;
    const next = state.results.map((result) => {
      if (result.kind === 'email') {
        if (result.email.id !== event.emailId) {
          return result;
        }
        changed = true;
        return { ...result, email: patch(result.email) as Email };
      }
      if (result.kind === 'thread') {
        const messages = result.thread.messages.map((message) => patch(message) as ThreadMessageWithStatus);
        const touched = messages.some((message, index) => message !== result.thread.messages[index]);
        if (!touched) {
          return result;
        }
        changed = true;
        return {
          ...result,
          thread: {
            ...result.thread,
            messages,
          },
        };
      }
      return result;
    });
    return changed ? { results: next } : {};
  });
}

function handleMailboxCounts(
  event: Extract<RealtimeEvent, { type: 'mailbox.counts' }>
  // ctx parameter removed - we no longer schedule reloads from count changes
) {
  useFolderStore.getState().updateCountsFromEvent(event);
  // (The per-counts-event emailCache invalidation was removed: counts change
  // on every flag flip and invalidating the instant-paint cache for them
  // defeated its purpose. Content events carry their own cache updates.)

  // Note: We do NOT reload the mailbox here. The counts are already updated above,
  // and individual email events (email.received, email.moved, email.deleted) handle
  // keeping the email list in sync. Reloading on every count change causes:
  // 1. Scroll position jumps during archive operations
  // 2. Race conditions with optimistic updates
  // 3. Unnecessary server load
  //
  // If we ever need to force a resync, it should be done on-demand (e.g., pull-to-refresh)
  // rather than automatically on every count change.
}

function handleFolderPreferencesUpdated(
  event: Extract<RealtimeEvent, { type: 'folder.preferences.updated' }>
) {
  if (!event.accountId) {
    return;
  }
  useFolderStore
    .getState()
    .loadFolders(event.accountId)
    .catch((error) => {
      console.error('Failed to refresh folders after realtime update', error);
    });
}

function handleMailboxListChanged(
  event: Extract<RealtimeEvent, { type: 'mailbox.list.changed' }>
) {
  if (!event.accountId) {
    return;
  }
  // Invalidate cache first to force a fresh fetch
  useFolderStore.getState().invalidateCacheForAccount(event.accountId);
  useFolderStore
    .getState()
    .loadFolders(event.accountId)
    .catch((error) => {
      console.error('Failed to refresh folders after mailbox list change', error);
    });
}

function handleContactStateChanged(
  event: Extract<RealtimeEvent, { type: 'contact.stateChanged' }>
) {
  const { contactId, approvedState, notificationsEnabled } = event;
  if (!contactId) {
    return;
  }

  mailEvents.emit('mail:contact:stateChanged', {
    contactId,
    approvedState,
    notificationsEnabled,
  });
}

function handleContactReviewRecommendation(
  event: Extract<RealtimeEvent, { type: 'contact.reviewRecommendation' }>
) {
  const { contactId } = event;
  if (!contactId) {
    return;
  }
  const recommendation = normalizeRealtimeReviewRecommendation(event.recommendation);
  mailEvents.emit('mail:contact:reviewUpdated', {
    contactId,
    recommendation,
  });
}

function normalizeRealtimeReviewRecommendation(
  raw: unknown
): ContactAiReviewRecommendation | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const statusValue = typeof record.status === 'string' ? record.status : null;
  if (!statusValue) {
    return null;
  }
  const decisionRaw = typeof record.decision === 'string'
    ? record.decision.trim().toLowerCase()
    : null;
  const decision = decisionRaw === 'yes' || decisionRaw === 'no' ? decisionRaw : null;
  const reason = typeof record.reason === 'string' ? record.reason : null;
  const updatedAt = typeof record.updatedAt === 'string' ? record.updatedAt : null;
  const messageId = typeof record.messageId === 'string' ? record.messageId : null;
  const idValue = record.id;
  const normalizedId =
    typeof idValue === 'string'
      ? idValue
      : typeof idValue === 'number'
        ? String(idValue)
        : undefined;

  return {
    id: normalizedId,
    status: statusValue as ContactAiReviewRecommendation['status'],
    decision,
    reason,
    updatedAt,
    messageId: messageId ?? undefined,
  };
}

function shouldDisplayEmail(email: Email): boolean {
  // Don't re-add emails that were recently removed (optimistic removal) — but
  // folder-scoped (R7/E8): only block the copy in the SOURCE folder it was
  // removed from, so the copy that moved to its destination shows immediately.
  if (isRecentlyRemoved(email.id, email.folderId)) {
    console.log('[shouldDisplayEmail] Blocking recently removed email:', email.id);
    return false;
  }

  // Don't add emails from senders that are being archived/deleted — but allow a
  // genuinely new message (date newer than the archive action) through.
  if (isSenderBeingArchived(email.from?.email, email.date, email.id)) {
    console.log('[shouldDisplayEmail] Blocking email from sender being archived:', email.from?.email, email.id);
    return false;
  }

  const accountId = useAccountStore.getState().selectedAccountId;
  const folderId = useFolderStore.getState().selectedFolderId();
  if (!accountId || !folderId) {
    return false;
  }
  const folder = useFolderStore.getState().folders.find((item) => item.id === folderId);
  if (!folder) {
    return false;
  }

  // For inbox and archive folders, filter out blocked senders and flagged surveys
  // This matches the server-side filtering in listMessages
  const isInboxOrArchive = folder.role === 'inbox' || folder.role === 'archive';
  if (isInboxOrArchive) {
    // Don't show emails from blocked senders
    if (email.contactApprovalState === 'blocked') {
      console.log('[shouldDisplayEmail] Blocking email from blocked sender:', email.from?.email, email.id);
      return false;
    }

    // Don't show flagged survey emails in inbox (they appear in the survey overview instead)
    if (folder.role === 'inbox' && email.isFlaggedSurvey) {
      console.log('[shouldDisplayEmail] Blocking flagged survey email:', email.id);
      return false;
    }
  }

  const sources = resolveFolderSources(folder, accountId, folderId);
  if (!sources.length) {
    if (accountId === UNIFIED_ACCOUNT_ID) {
      return false;
    }
    return email.accountId === accountId && email.folderId === folderId;
  }
  return sources.some(
    (source) => source.accountId === email.accountId && source.folderId === email.folderId
  );
}

function resolveFolderSources(
  folder: { sourceFolders?: Array<{ accountId: string; folderId: string }>; accountId?: string; id: string },
  selectedAccountId: string,
  selectedFolderId: string
) {
  if (folder.sourceFolders?.length) {
    return folder.sourceFolders;
  }
  if (folder.accountId && folder.accountId !== UNIFIED_ACCOUNT_ID) {
    return [{ accountId: folder.accountId, folderId: folder.id }];
  }
  if (selectedAccountId !== UNIFIED_ACCOUNT_ID) {
    return [{ accountId: selectedAccountId, folderId: selectedFolderId }];
  }
  return [];
}

function applyDownloadedAttachmentState(email: Email): Email {
  if (!email.attachments?.length || !email.accountId) {
    return email;
  }
  const { downloadedByEmail } = useAttachmentStore.getState();
  const downloaded = downloadedByEmail[`${email.accountId}:${email.id}`];
  if (!downloaded?.size) {
    return email;
  }
  let changed = false;
  const attachments = email.attachments.map((attachment) => {
    if (downloaded.has(attachment.id) && !attachment.downloaded) {
      changed = true;
      return { ...attachment, downloaded: true };
    }
    return attachment;
  });
  return changed ? { ...email, attachments } : email;
}

function updateThreadMessage(thread: ReturnType<typeof useEmailStore.getState>['selectedThread'], email: Email) {
  if (!thread) {
    return thread;
  }
  const hasMessage = thread.messages.some((message) => message.id === email.id);
  if (!hasMessage) {
    return thread;
  }
  const messages = thread.messages.map((message) =>
    message.id === email.id ? mergeEmailUpdate(message, email) : message
  );
  const unreadCount = messages.filter((message) => !message.read).length;
  return {
    ...thread,
    messages,
    unreadCount,
  };
}

function updateSearchResultsWithEmail(email: Email) {
  useSearchStore.setState((state) => {
    let changed = false;
    const results = state.results.map((result) => {
      if (result.kind === 'email' && result.email.id === email.id) {
        changed = true;
        return { ...result, email: mergeEmailUpdate(result.email, email) };
      }
      if (result.kind === 'thread' && result.thread.accountId === email.accountId) {
        let threadChanged = false;
        const messages = result.thread.messages.map((message) => {
          if (message.id !== email.id) {
            return message;
          }
          threadChanged = true;
          return mergeEmailUpdate(message, email);
        });
        if (threadChanged) {
          changed = true;
          return {
            ...result,
            thread: {
              ...result.thread,
              messages,
              unreadCount: messages.filter((message) => !message.read).length,
            },
          };
        }
      }
      return result;
    });
    return changed ? { results } : {};
  });
}

function updateSearchResultsForMovedEmail(
  event: Extract<RealtimeEvent, { type: 'email.moved' }>
) {
  useSearchStore.setState((state) => {
    let changed = false;
    const next = state.results.map((result) => {
      if (result.kind === 'email' && result.email.id === event.emailId) {
        changed = true;
        return {
          ...result,
          email: { ...result.email, folderId: event.toFolderId },
        };
      }
      if (result.kind === 'thread' && result.thread.accountId === event.accountId) {
        let threadChanged = false;
        const messages = result.thread.messages.map((message) => {
          if (message.id !== event.emailId) {
            return message;
          }
          threadChanged = true;
          return { ...message, folderId: event.toFolderId };
        });
        if (!threadChanged) {
          return result;
        }
        changed = true;
        return {
          ...result,
          thread: {
            ...result.thread,
            messages,
          },
        };
      }
      return result;
    });
    return changed ? { results: next } : {};
  });
}

function removeSearchResultsByEmailIds(ids: Set<string>, accountId: string) {
  if (!ids.size) {
    return;
  }
  useSearchStore.setState((state) => {
    let changed = false;
    const next: typeof state.results = [];
    state.results.forEach((result) => {
      if (result.kind === 'email') {
        if (ids.has(result.email.id) && result.accountId === accountId) {
          changed = true;
          return;
        }
        next.push(result);
        return;
      }
      if (result.kind === 'thread' && result.accountId === accountId) {
        const messages = result.thread.messages.filter((message) => !ids.has(message.id));
        if (messages.length === 0) {
          changed = true;
          return;
        }
        if (messages.length !== result.thread.messages.length) {
          changed = true;
          next.push({
            ...result,
            thread: {
              ...result.thread,
              messages,
              messageCount: messages.length,
              unreadCount: messages.filter((message) => !message.read).length,
            },
          });
          return;
        }
      }
      next.push(result);
    });
    return changed ? { results: next } : {};
  });
}

function pruneBulkSelection(emailIds: string[]) {
  if (!emailIds.length) {
    return;
  }
  const idSet = new Set(emailIds);
  useBulkOperationsStore.setState((state) => {
    const next = new Set(state.bulkSelection);
    let changed = false;
    idSet.forEach((id) => {
      if (next.delete(id)) {
        changed = true;
      }
    });
    return changed ? { bulkSelection: next } : {};
  });
}

function pruneSenderDigestEmails(emailIds: string[]) {
  if (!emailIds.length) {
    return;
  }
  const idSet = new Set(emailIds);
  useSenderGroupingStore.setState((state) => {
    if (!state.senderDigest) {
      return {};
    }
    const filtered = state.senderDigest.emails.filter((email) => !idSet.has(email.id));
    if (filtered.length === state.senderDigest.emails.length) {
      return {};
    }
    if (!filtered.length) {
      return { senderDigest: null };
    }
    const removed = state.senderDigest.emails.length - filtered.length;
    return {
      senderDigest: {
        ...state.senderDigest,
        emails: filtered,
        unreadCount: filtered.filter((email) => !email.read).length,
        totalMessages: Math.max(state.senderDigest.totalMessages - removed, filtered.length),
      },
    };
  });
}

function updateSenderDigestEmail(email: Email) {
  useSenderGroupingStore.setState((state) => {
    if (!state.senderDigest) {
      return {};
    }
    const emails = state.senderDigest.emails.map((entry) => {
      if (entry.id !== email.id) {
        return entry;
      }
      // Use mergeEmailUpdate to preserve existing values for properties
      // that are undefined in the incoming email (e.g., bodyHtml from realtime events)
      return mergeEmailUpdate(entry, email);
    });
    const touched = emails.some((entry, index) => entry !== state.senderDigest!.emails[index]);
    if (!touched) {
      return {};
    }
    return {
      senderDigest: {
        ...state.senderDigest,
        emails,
        unreadCount: emails.filter((item) => !item.read).length,
      },
    };
  });
}

function setAttachmentDownloadedFlag<
  T extends { attachments?: { id: string; downloaded?: boolean; filename?: string; mimeType?: string | null; size?: number | null }[] }
>(
  entity: T,
  attachmentId: string,
  metadata?: { filename?: string | null; mimeType?: string | null; size?: number | null }
): T | null {
  if (!entity.attachments?.length) {
    return null;
  }
  let changed = false;
  const attachments = entity.attachments.map((attachment) => {
    if (attachment.id !== attachmentId) {
      return attachment;
    }
    const next = {
      ...attachment,
      downloaded: true,
      filename: metadata?.filename ?? attachment.filename,
      mimeType: metadata?.mimeType ?? attachment.mimeType,
      size: metadata?.size ?? attachment.size,
    };
    if (
      !attachment.downloaded ||
      attachment.filename !== next.filename ||
      attachment.mimeType !== next.mimeType ||
      (typeof metadata?.size === 'number' && metadata.size !== attachment.size)
    ) {
      changed = true;
      return next;
    }
    return attachment.downloaded ? attachment : next;
  });
  return changed ? { ...entity, attachments } : null;
}

// ============================================
// Calendar Event Handlers
// ============================================

function handleCalendarEventUpsert(
  event: Extract<RealtimeEvent, { type: 'calendar.event.created' | 'calendar.event.updated' }>
) {
  if (!event.event) {
    return;
  }

  const calendarStore = useCalendarStore.getState();
  // The event from SSE should already be in CalendarEventModel format from the backend
  // The upsertEvents function will handle normalization
  calendarStore.upsertEvents([event.event as unknown as import('@/types/calendar').CalendarEventModel]);
}

function handleCalendarEventDeleted(
  event: Extract<RealtimeEvent, { type: 'calendar.event.deleted' }>
) {
  const eventId = event.eventId;

  if (!eventId) {
    return;
  }

  const calendarStore = useCalendarStore.getState();
  calendarStore.removeEvent(eventId);
}

// ============================================
// Note Event Handlers
// ============================================

function handleNoteUpserted(
  event: Extract<RealtimeEvent, { type: 'note.created' | 'note.updated' }>
) {
  if (!event.note) {
    return;
  }

  // Visibility (open/archived/deleted) is filtered at render time, so a
  // plain upsert is correct for every lifecycle transition.
  const notesStore = useNotesStore.getState();
  notesStore.upsertNote(event.note);
}

function handleNoteDeleted(
  event: Extract<RealtimeEvent, { type: 'note.deleted' }>
) {
  if (!event.noteId) {
    return;
  }

  const notesStore = useNotesStore.getState();
  notesStore.removeNote(event.noteId);
}

function handleNoteTagUpsert(
  event: Extract<RealtimeEvent, { type: 'note.tag.created' | 'note.tag.updated' }>
) {
  if (!event.tag) {
    return;
  }

  const notesStore = useNotesStore.getState();
  notesStore.upsertTag(event.tag);
}

function handleNoteTagDeleted(
  event: Extract<RealtimeEvent, { type: 'note.tag.deleted' }>
) {
  if (!event.tagId) {
    return;
  }

  const notesStore = useNotesStore.getState();
  notesStore.removeTag(event.tagId);
}

function handleOperationFailed(
  event: Extract<RealtimeEvent, { type: 'operation.failed' }>
) {
  import('sonner').then(({ toast }) => {
    toast.error(event.message || 'An operation failed.');
  });
}
